import { APP_VERSION } from '@/constants/app-version';
import { CALENDAR_SYNC_LOOKAHEAD_DAYS } from '@/constants/calendar';
import { authService } from '@/services/auth/auth-service';
import type { OutageBanner } from '@/types/app-config';
import {
  CalendarEventsResponse,
  CalendarSyncRequest,
  CalendarSyncResponse,
} from '@/types/calendar';
import {
  ChannelPartner,
  ChannelPartnerDashboard,
} from '@/types/channel-partner';
import {
  ChatCapabilitiesResponse,
  ChatRequest,
  ChatResponse,
  QuickChatRequest,
  QuickChatResponse,
} from '@/types/chat';
import {
  Company,
  CompanyDocument,
  CompanyUsersResponse,
  GetCompanyUsersRequest,
  InviteUsersToCompanyRequest,
  InviteUsersToCompanyResponse,
} from '@/types/company';
import {
  CompanyDisclosureRequiredResponse,
  CompanyDisclosureSettings,
  UpdateCompanyDisclosureRequiredRequest,
  UpdateUserDisclosureRequiredRequest,
} from '@/types/disclosure';
import { AggregateInsight } from '@/types/insight';
import {
  GoogleIntegration,
  IntegrationAccount,
  MicrosoftIntegration,
  ZoomIntegration,
} from '@/types/integration';
import {
  LocalizationSettings,
  UpdateLocalizationRequest,
} from '@/types/localization';
import {
  Meeting,
  MeetingDimensionalReportsAggregated,
  MeetingLeadershipResonanceAggregated,
  RecentMeeting,
  UpdateMeetingRequest,
} from '@/types/meeting';
import { RoleBasedDashboard } from '@/types/multi-tenant';
import { Objective } from '@/types/objective';
import { PaginatedResponse } from '@/types/pagination';
import { PerformanceLoopResponse } from '@/types/performance-loop';
import {
  CombinedTableQueryParams,
  CombinedTableResponse,
  RecentSession,
  SessionAnalysisResponse,
  SessionDetailedReport,
  SessionDimensionalReport,
  UpdateSessionRequest,
} from '@/types/session';
import { Team } from '@/types/teams';
import { Transcript } from '@/types/transcript';
import {
  BulkUserOperationRequest,
  BulkUserOperationResponse,
  DeactivateUserRequest,
  UpdateUserRoleRequest,
  User,
} from '@/types/user';
import { getOrCreateClientUuid } from '@/utils/client-uuid';
import { createApi } from '@reduxjs/toolkit/query/react';
import snakeCaseKeys from 'snakecase-keys';

import {
  baseQueryWithIntercept,
  isClientVersionErrorCode,
  redirectToInvalidClient,
} from './base';
import { multiTenantEndpoints } from './multi-tenant';

type StreamMetrics = {
  processing_time_ms: number;
  tool_calls: number;
};

type StreamEvent =
  | { type: 'chunk'; content: string }
  | { type: 'replace'; content: string }
  | { type: 'warning'; content: string[] }
  | { type: 'done'; metrics: StreamMetrics }
  | { type: 'error'; content: string };

export const piloteerApi = createApi({
  reducerPath: 'piloteerApi',
  baseQuery: baseQueryWithIntercept,
  tagTypes: [
    'Coach',
    'IntegrationAccounts',
    'GoogleCalendars',
    'User',
    'CalendarEvents',
    'ChannelPartner',
    'Company',
    'Team',
    'Analytics',
    'SuperAdmin',
    'Sessions',
    'CompanyDocuments',
    'AppConfig',
    'DesktopClients',
    'UserClients',
    'UserCredits',
    'CompanyOverview',
    'CompanyModelRoutes',
    'TeamModelRoutes',
    'UserRecentSessions',
  ],
  endpoints: (builder) => ({
    check: builder.query<void, void>({
      query: () => 'check',
    }),

    getOutageBanner: builder.query<OutageBanner, void>({
      query: () => 'app-config/outage-banner',
      providesTags: ['AppConfig'],
      keepUnusedDataFor: 60,
    }),

    // User endpoints
    getUser: builder.query<User, void>({
      query: () => 'users/',
      providesTags: ['User'],
      keepUnusedDataFor: 1800,
    }),
    updateUser: builder.mutation<User, Partial<User>>({
      query: (user) => ({
        url: 'users/',
        method: 'PATCH',
        body: snakeCaseKeys(user, { deep: true }),
      }),
      invalidatesTags: ['User', 'Analytics', 'Coach'],
    }),
    updateProfilePicture: builder.mutation<User, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/users/profile-picture',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['User', 'Analytics', 'Coach'],
    }),

    // Meeting endpoints
    getAllMeetings: builder.query<Meeting[], void>({
      query: () => 'meetings',
    }),
    getRecentMeetings: builder.query<RecentMeeting[], void>({
      query: () => 'meetings/recent',
      keepUnusedDataFor: 1800,
    }),
    getMeetingByUuid: builder.query<Meeting, string>({
      query: (uuid) => `meetings/${uuid}`,
      keepUnusedDataFor: 1800,
    }),
    getPublicMeetingByUuid: builder.query<Meeting, string>({
      query: (uuid) => `meetings/${uuid}/public`,
      keepUnusedDataFor: 1800,
    }),
    getMeetingTranscript: builder.query<
      Transcript,
      { uuid: string; public: boolean }
    >({
      query: ({ uuid, public: isPublic }) =>
        `meetings/${uuid}/transcript${isPublic ? '/public' : ''}`,
    }),

    getLeadershipResonancesAggregated: builder.query<
      MeetingLeadershipResonanceAggregated,
      void
    >({
      query: () => `meetings/leadership-resonances/aggregate`,
      keepUnusedDataFor: 1800,
    }),
    getDimensionalReportsAggregated: builder.query<
      MeetingDimensionalReportsAggregated,
      void
    >({
      query: () => `meetings/dimensional-reports/aggregate`,
    }),
    updateMeeting: builder.mutation<Meeting, UpdateMeetingRequest>({
      query: (meeting) => ({
        url: `meetings/${meeting.meetingUuid}`,
        method: 'PUT',
        body: {
          ...(meeting.title !== undefined && { title: meeting.title }),
          ...(meeting.isPublic !== undefined && {
            is_public: meeting.isPublic,
          }),
        },
      }),
      async onQueryStarted(
        { meetingUuid, title, isPublic },
        { dispatch, queryFulfilled }
      ) {
        // Optimistic update
        const patchResult = dispatch(
          piloteerApi.util.updateQueryData(
            'getMeetingByUuid',
            meetingUuid,
            (draft) => {
              if (title !== undefined) {
                draft.title = title;
              }
              if (isPublic !== undefined) {
                draft.isPublic = isPublic;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResult.undo();
        }
      },
    }),

    // Session endpoints
    getCombinedTable: builder.query<
      CombinedTableResponse,
      CombinedTableQueryParams | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        const {
          sessionType,
          hasEmotions,
          completedOnly = true,
          daysBack,
          fromDate,
          toDate,
          search,
          role,
          analysisStatus,
          page = 1,
          perPage = 20,
        } = params || {};

        if (sessionType) searchParams.set('session_type', sessionType);
        if (hasEmotions !== undefined) {
          searchParams.set('has_emotions', String(hasEmotions));
        }
        if (completedOnly !== undefined) {
          searchParams.set('completed_only', String(completedOnly));
        }
        if (daysBack) searchParams.set('days_back', String(daysBack));
        if (fromDate) searchParams.set('from_date', fromDate);
        if (toDate) searchParams.set('to_date', toDate);
        if (search) searchParams.set('search', search);
        if (role) searchParams.set('role', role);
        if (analysisStatus) {
          searchParams.set('analysis_status', analysisStatus);
        }
        searchParams.set('page', String(page));
        searchParams.set('per_page', String(perPage));

        return `sessions/combined-table?${searchParams.toString()}`;
      },
      providesTags: ['Sessions'],
      keepUnusedDataFor: 1800,
    }),
    getRecentSessions: builder.query<RecentSession[], void>({
      query: () => 'sessions/recent',
      providesTags: ['Sessions'],
    }),
    getSessionByUuid: builder.query<SessionDetailedReport, string>({
      query: (uuid) => `sessions/${uuid}/report`,
    }),
    getPublicSessionByUuid: builder.query<SessionDetailedReport, string>({
      query: (uuid) => `sessions/${uuid}/report/public`,
    }),
    getSessionDimensionalReports: builder.query<
      SessionDimensionalReport[],
      string
    >({
      query: (uuid) => `sessions/${uuid}/dimensional-reports`,
    }),
    getSessionAnalysis: builder.query<SessionAnalysisResponse, string>({
      query: (uuid) => `sessions/${uuid}/analysis`,
    }),
    updateSession: builder.mutation<
      SessionDetailedReport,
      UpdateSessionRequest
    >({
      query: (session) => ({
        url: `sessions/${session.sessionUuid}`,
        method: 'PUT',
        body: {
          ...(session.title !== undefined && { title: session.title }),
          ...(session.isPublic !== undefined && {
            is_public: session.isPublic,
          }),
        },
      }),
      async onQueryStarted(
        { sessionUuid, title, isPublic },
        { dispatch, queryFulfilled }
      ) {
        // Optimistic update
        const patchResult = dispatch(
          piloteerApi.util.updateQueryData(
            'getSessionByUuid',
            sessionUuid,
            (draft) => {
              if (title !== undefined) {
                draft.title = title;
              }
              if (isPublic !== undefined) {
                draft.isPublic = isPublic;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResult.undo();
        }
      },
      invalidatesTags: ['Sessions', 'Analytics'],
    }),

    // Objective endpoints
    getLatestObjectives: builder.query<Objective[], void>({
      query: () => 'objectives/latest',
    }),

    // Integration endpoints
    getIntegrationAccount: builder.query<
      IntegrationAccount,
      'microsoft' | 'zoom'
    >({
      query: (provider) => `integrations/accounts/${provider}`,
      providesTags: (_, __, provider) => [
        { type: 'IntegrationAccounts', id: provider },
      ],
      keepUnusedDataFor: 300,
    }),
    connectZoomAccount: builder.mutation<void, ZoomIntegration>({
      query: (zoomIntegration) => ({
        url: 'integrations/zoom/connect',
        method: 'POST',
        body: snakeCaseKeys(zoomIntegration, { deep: true }),
      }),
      invalidatesTags: ['User', { type: 'IntegrationAccounts', id: 'zoom' }],
    }),

    disconnectZoomAccount: builder.mutation<void, void>({
      query: () => ({
        url: 'integrations/zoom/disconnect/',
        method: 'POST',
      }),
      invalidatesTags: ['User', { type: 'IntegrationAccounts', id: 'zoom' }],
    }),

    authorizeGoogleCalendar: builder.mutation<
      { authorizationUrl: string },
      void
    >({
      query: () => 'integrations/calendar/google/authorize',
    }),
    getGoogleCalendars: builder.query<
      {
        calendars: {
          id: string;
          summary: string;
          primary: boolean;
          selected: boolean;
        }[];
      },
      void
    >({
      query: () => 'integrations/calendar/google/calendars',
      providesTags: ['GoogleCalendars'],
    }),
    selectGoogleCalendar: builder.mutation<void, { calendarId: string }>({
      query: (selection) => ({
        url: 'integrations/calendar/google/calendars',
        method: 'PUT',
        body: snakeCaseKeys(selection),
      }),
      invalidatesTags: ['GoogleCalendars', 'CalendarEvents'],
    }),
    connectGoogleCalendar: builder.mutation<void, GoogleIntegration>({
      query: (googleIntegration) => ({
        url: 'integrations/calendar/google/connect',
        method: 'POST',
        body: snakeCaseKeys(googleIntegration, { deep: true }),
      }),
      invalidatesTags: ['User', 'GoogleCalendars', 'CalendarEvents'],
    }),

    disconnectGoogleCalendar: builder.mutation<void, void>({
      query: () => ({
        url: 'integrations/calendar/google/disconnect',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'GoogleCalendars', 'CalendarEvents'],
    }),

    connectMicrosoftCalendar: builder.mutation<void, MicrosoftIntegration>({
      query: (microsoftIntegration) => ({
        url: 'integrations/calendar/microsoft/connect',
        method: 'POST',
        body: snakeCaseKeys(microsoftIntegration, { deep: true }),
      }),
      invalidatesTags: [
        'User',
        { type: 'IntegrationAccounts', id: 'microsoft' },
      ],
    }),

    disconnectMicrosoftCalendar: builder.mutation<void, void>({
      query: () => ({
        url: 'integrations/calendar/microsoft/disconnect',
        method: 'POST',
      }),
      invalidatesTags: [
        'User',
        { type: 'IntegrationAccounts', id: 'microsoft' },
      ],
    }),

    // Calendar event endpoints
    getCalendarEvents: builder.query<CalendarEventsResponse, { days: number }>({
      query: ({ days }) => `calendars/events?days=${days}`,
      providesTags: ['CalendarEvents'],
    }),
    syncCalendar: builder.mutation<
      CalendarSyncResponse,
      CalendarSyncRequest | void
    >({
      query: (syncRequest) => ({
        url: 'calendars/sync',
        method: 'POST',
        body: syncRequest,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            piloteerApi.util.updateQueryData(
              'getCalendarEvents',
              { days: CALENDAR_SYNC_LOOKAHEAD_DAYS },
              (draft) => {
                draft.events = data.events;
              }
            )
          );
        } catch {
          // Component-level handlers show the user-facing sync error.
        }
      },
    }),
    // Multi-tenant endpoints
    // Channel Partner endpoints
    getChannelPartners: builder.query<PaginatedResponse<ChannelPartner>, void>(
      multiTenantEndpoints.getChannelPartners
    ),
    getChannelPartnerById: builder.query<ChannelPartner, string>(
      multiTenantEndpoints.getChannelPartnerById
    ),
    createChannelPartner: builder.mutation<
      ChannelPartner,
      Partial<ChannelPartner>
    >(multiTenantEndpoints.createChannelPartner.mutation),
    updateChannelPartner: builder.mutation<
      ChannelPartner,
      Partial<ChannelPartner> & { uuid: string }
    >(multiTenantEndpoints.updateChannelPartner.mutation),
    getCompaniesByChannelPartner: builder.query<Company[], string>(
      multiTenantEndpoints.getCompaniesByChannelPartner
    ),

    // Channel Partner Dashboard endpoints
    getChannelPartnerDashboard: builder.query<
      ChannelPartnerDashboard,
      { startDate?: string; endDate?: string } | undefined
    >(multiTenantEndpoints.getChannelPartnerDashboard),
    getChannelPartnerDashboardById: builder.query<
      ChannelPartnerDashboard,
      {
        partnerUuid: string;
        params?: { startDate?: string; endDate?: string };
      }
    >({
      query: ({ partnerUuid, params }) =>
        multiTenantEndpoints.getChannelPartnerDashboardById.query(
          partnerUuid,
          params
        ),
      providesTags: (_result, _error, { partnerUuid }) => [
        { type: 'ChannelPartner', uuid: partnerUuid },
        'Analytics',
      ],
    }),

    // Company endpoints
    getCompanies: builder.query<Company[], void>(
      multiTenantEndpoints.getCompanies
    ),
    getCompanyById: builder.query<Company, string>(
      multiTenantEndpoints.getCompanyById
    ),
    createCompany: builder.mutation<Company, Partial<Company>>(
      multiTenantEndpoints.createCompany.mutation
    ),
    updateCompany: builder.mutation<
      Company,
      Partial<Company> & { uuid: string }
    >(multiTenantEndpoints.updateCompany.mutation),
    getCompanyStats: builder.query<
      { totalUsers: number; totalTeams: number; activeUsers: number },
      string
    >(multiTenantEndpoints.getCompanyStats),

    // Team endpoints
    getTeams: builder.query<Team[], string | undefined>(
      multiTenantEndpoints.getTeams
    ),
    getTeamById: builder.query<Team, string>(multiTenantEndpoints.getTeamById),
    createTeam: builder.mutation<Team, Partial<Team>>(
      multiTenantEndpoints.createTeam.mutation
    ),
    updateTeam: builder.mutation<Team, Partial<Team> & { uuid: string }>(
      multiTenantEndpoints.updateTeam.mutation
    ),
    addTeamMember: builder.mutation<void, { teamUuid: string; userId: string }>(
      multiTenantEndpoints.addTeamMember.mutation
    ),
    removeTeamMember: builder.mutation<
      void,
      { teamUuid: string; userId: string }
    >(multiTenantEndpoints.removeTeamMember.mutation),

    // Company User endpoints
    getCompanyUsers: builder.query<
      CompanyUsersResponse,
      GetCompanyUsersRequest
    >(multiTenantEndpoints.getCompanyUsers),
    inviteUsersToCompany: builder.mutation<
      InviteUsersToCompanyResponse,
      InviteUsersToCompanyRequest
    >(multiTenantEndpoints.inviteUsersToCompany.mutation),
    updateUserRole: builder.mutation<
      { success: boolean; message: string },
      UpdateUserRoleRequest
    >(multiTenantEndpoints.updateUserRole.mutation),
    bulkUserOperations: builder.mutation<
      BulkUserOperationResponse,
      BulkUserOperationRequest
    >(multiTenantEndpoints.bulkUserOperations.mutation),
    resendUserInvitation: builder.mutation<
      {
        userUuid: string;
        email: string;
        resendSuccessful: boolean;
        message?: string | null;
        error?: string | null;
      },
      string
    >(multiTenantEndpoints.resendUserInvitation.mutation),
    deactivateUser: builder.mutation<
      { success: boolean; message: string },
      DeactivateUserRequest
    >(multiTenantEndpoints.deactivateUser.mutation),
    activateUser: builder.mutation<
      { success: boolean; message: string },
      string
    >(multiTenantEndpoints.activateUser.mutation),
    updateUserDisclosureRequired: builder.mutation<
      User,
      UpdateUserDisclosureRequiredRequest
    >(multiTenantEndpoints.updateUserDisclosureRequired.mutation),
    getCompanyDisclosureSettings: builder.query<
      CompanyDisclosureSettings,
      string
    >(multiTenantEndpoints.getCompanyDisclosureSettings),
    updateCompanyDisclosureRequired: builder.mutation<
      CompanyDisclosureRequiredResponse,
      UpdateCompanyDisclosureRequiredRequest
    >(multiTenantEndpoints.updateCompanyDisclosureRequired.mutation),

    // Analytics endpoint
    getRoleBasedDashboard: builder.query<RoleBasedDashboard, void>(
      multiTenantEndpoints.getRoleBasedDashboard
    ),

    // Onboarding endpoints (for channel partner admins)
    onboardCompany: builder.mutation<
      {
        company: Company;
        adminUser: {
          email: string;
          name: string;
          temporaryPassword: string;
        };
      },
      {
        companyName: string;
        companyDomain?: string;
        industry: string;
        size: 'small' | 'medium' | 'large' | 'enterprise';
        timezone: string;
        subscriptionTier: string;
        maxUsers: number;
        maxMeetingsPerMonth: number;
        adminEmail: string;
        adminName: string;
        adminPhone?: string;
      }
    >({
      query: (data) => ({
        url: 'admin/onboarding/company',
        method: 'POST',
        body: snakeCaseKeys(
          {
            companyName: data.companyName,
            companyDomain: data.companyDomain,
            industry: data.industry,
            size: data.size,
            timezone: data.timezone,
            subscriptionTier: data.subscriptionTier,
            maxUsers: data.maxUsers,
            maxMeetingsPerMonth: data.maxMeetingsPerMonth,
            adminEmail: data.adminEmail,
            adminName: data.adminName,
            adminPhone: data.adminPhone,
          },
          { deep: true }
        ),
      }),
      invalidatesTags: ['Company', 'Analytics'],
    }),

    addUsersToCompany: builder.mutation<
      {
        created: Array<{
          email: string;
          name: string;
          temporaryPassword: string;
        }>;
        errors: Array<{
          email: string;
          error: string;
        }>;
      },
      {
        companyUuid: string;
        users: Array<{
          email: string;
          name: string;
          role: 'company_admin' | 'company_manager' | 'company_member';
        }>;
      }
    >({
      query: ({ companyUuid, users }) => ({
        url: `admin/onboarding/company/${companyUuid}/users`,
        method: 'POST',
        body: users,
      }),
      invalidatesTags: (_result, _error, { companyUuid }) => [
        { type: 'Company', uuid: companyUuid },
        'Analytics',
      ],
    }),

    getMyOnboardedCompanies: builder.query<
      {
        companies: Company[];
        total: number;
      },
      void
    >({
      query: () => 'admin/onboarding/companies/my',
      providesTags: ['Company'],
    }),

    getOnboardedCompanyDetails: builder.query<Company, string>({
      query: (uuid) => `/companies/${uuid}`,
      providesTags: (_result, _error, uuid) => [
        { type: 'Company' as const, uuid },
      ],
    }),

    // Performance Loop endpoints
    getPerformanceLoop: builder.query<
      PerformanceLoopResponse,
      { entityType: string; entityUuid: string; periodDays?: number }
    >({
      query: ({ entityType, entityUuid, periodDays = 30 }) =>
        `performance-loop/${entityType}/${entityUuid}?period_days=${periodDays}`,
      keepUnusedDataFor: 1800,
    }),

    // Insight endpoints
    getMyAggregateInsight: builder.query<AggregateInsight, void>({
      query: () => `insights/my/latest`,
      keepUnusedDataFor: 1800,
    }),
    getCompanyAggregateInsight: builder.query<
      AggregateInsight,
      string | { uuid: string; periodDays?: number }
    >({
      query: (arg) => {
        const { uuid, periodDays } =
          typeof arg === 'string' ? { uuid: arg, periodDays: undefined } : arg;
        const suffix = periodDays ? `?period_days=${periodDays}` : '';
        return `insights/company/${uuid}/latest${suffix}`;
      },
      keepUnusedDataFor: 1800,
    }),
    getTeamAggregateInsight: builder.query<
      AggregateInsight,
      string | { uuid: string; periodDays?: number }
    >({
      query: (arg) => {
        const { uuid, periodDays } =
          typeof arg === 'string' ? { uuid: arg, periodDays: undefined } : arg;
        const suffix = periodDays ? `?period_days=${periodDays}` : '';
        return `insights/team/${uuid}/latest${suffix}`;
      },
      keepUnusedDataFor: 1800,
    }),
    getUserAggregateInsight: builder.query<AggregateInsight, string>({
      query: (uuid) => `insights/individual/${uuid}/latest`,
      keepUnusedDataFor: 1800,
    }),

    // Chat endpoints
    quickChat: builder.mutation<QuickChatResponse, QuickChatRequest>({
      query: (body) => ({
        url: 'chat/quick',
        method: 'POST',
        body: { message: body.message },
      }),
    }),
    chat: builder.mutation<ChatResponse, ChatRequest>({
      query: (body) => ({
        url: 'chat',
        method: 'POST',
        body: {
          message: body.message,
          conversation_history: body.conversationHistory?.map((msg) => ({
            role: msg.role,
            content: msg.content,
          })),
          include_sources: body.includeSources,
        },
      }),
    }),

    chatStream: builder.mutation<void, ChatRequest>({
      async queryFn(body) {
        try {
          const token = await authService.getAccessToken();
          const baseUrl = import.meta.env.VITE_PILOTEER_API_BASE_URL;

          if (!token) {
            throw new Error('Authentication required');
          }

          const response = await fetch(`${baseUrl}/chat/stream`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-App-Version': APP_VERSION,
              'X-Client-UUID': getOrCreateClientUuid(),
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              message: body.message,
              conversation_history: body.conversationHistory?.map((msg) => ({
                role: msg.role,
                content: msg.content,
              })),
              include_sources: body.includeSources,
            }),
            signal: body.signal,
          });
          if (!response.ok) {
            let errorCode: string | undefined;
            try {
              const errorData = (await response.json()) as {
                error_code?: string;
              };
              errorCode = errorData.error_code;
            } catch {
              // Ignore non-JSON failures and fall back to generic handling below.
            }

            if (isClientVersionErrorCode(errorCode)) {
              redirectToInvalidClient();
            }

            throw new Error('Stream request failed');
          }
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) throw new Error('No reader available');

          let buffer = '';
          let assistantContent = '';

          const processLine = (line: string) => {
            const trimmedLine = line.trim();
            if (!trimmedLine.startsWith('data: ')) return;

            try {
              const data: StreamEvent = JSON.parse(trimmedLine.slice(6));
              if (data.type === 'chunk') {
                assistantContent += data.content;
                body.onChunk?.(data.content);
                return;
              }

              if (data.type === 'replace') {
                assistantContent = data.content;
                body.onReplace?.(data.content);
                return;
              }

              if (data.type === 'warning') {
                console.warn('Chat stream warning:', data.content);
                return;
              }

              if (data.type === 'error') {
                throw new Error(data.content || 'Stream request failed');
              }
            } catch (e) {
              if (e instanceof Error) {
                throw e;
              }
              console.error('Error parsing stream data:', e);
            }
          };

          let result = await reader.read();
          while (!result.done) {
            buffer += decoder.decode(result.value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              processLine(line);
            }
            result = await reader.read();
          }

          buffer += decoder.decode();
          if (buffer.trim()) {
            processLine(buffer);
          }

          if (!assistantContent) {
            throw new Error('Stream completed without assistant content');
          }

          return { data: undefined };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', error: String(error) } };
        }
      },
    }),
    getChatCapabilities: builder.query<ChatCapabilitiesResponse, void>({
      query: () => 'chat/capabilities',
    }),

    // Localization endpoints
    getCompanyLocalization: builder.query<LocalizationSettings, string>({
      query: (companyUuid) => `companies/${companyUuid}/localization`,
      providesTags: (_result, _error, companyUuid) => [
        { type: 'Company', id: `${companyUuid}-localization` },
      ],
    }),
    updateCompanyLocalization: builder.mutation<
      LocalizationSettings,
      UpdateLocalizationRequest
    >({
      query: ({ companyUuid, ...body }) => ({
        url: `companies/${companyUuid}/localization`,
        method: 'PUT',
        body: snakeCaseKeys(body, { deep: true }),
      }),
      invalidatesTags: (_result, _error, { companyUuid }) => [
        { type: 'Company', id: `${companyUuid}-localization` },
        'Analytics',
      ],
    }),

    // Company Document endpoints
    getCompanyDocuments: builder.query<CompanyDocument[], string>({
      query: (companyUuid) => `companies/${companyUuid}/documents`,
      providesTags: ['CompanyDocuments'],
    }),
    uploadCompanyDocument: builder.mutation<
      CompanyDocument,
      { companyUuid: string; file: File }
    >({
      query: ({ companyUuid, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `companies/${companyUuid}/documents`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['CompanyDocuments'],
    }),
    deleteCompanyDocument: builder.mutation<void, string>({
      query: (documentUuid) => ({
        url: `companies/documents/${documentUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CompanyDocuments'],
    }),
  }),
});

export const {
  useGetCompanyLocalizationQuery,
  useUpdateCompanyLocalizationMutation,
} = piloteerApi;
